import { mergeClasses, Tooltip } from "@fluentui/react-components";
import {
  Delete20Regular,
  Edit20Regular,
  Eye20Regular,
} from "@fluentui/react-icons";
import { deleteCategory } from "api";
import { ActionDialog } from "components/actionDialog/ActionDialog";
import { AdminListPage } from "components/adminList/AdminListPage";
import { AdminRowAction } from "components/adminList/AdminListPage.types";
import { useAdminColumnStyles } from "components/adminList/AdminTable.styles";
import { findSidebarItem } from "components/adminSidebar/adminSidebarConfig";
import { useAdminListFilters } from "hooks/useAdminListFilters";
import { useAdminListPagination } from "hooks/useAdminListPagination";
import { useAdminListSearch } from "hooks/useAdminListSearch";
import { useAdminListSort } from "hooks/useAdminListSort";
import { ChevronIcon } from "icons/ChevronIcon";
import { ExportIcon } from "icons/ExportIcon";
import { PlusIcon } from "icons/PlusIcon";
import { SearchIcon } from "icons/SearchIcon";
import { ICategory } from "models/ICategory";
import { ITableColumn } from "models/ITableColumn";
import { ITag } from "models/ITag";
import { useCallback, useMemo, useState } from "react";
import { useQueryClient } from "react-query";
import { useSelector } from "react-redux";
import { RootState } from "store";
import {
  useSharedAdminContentHeaderStyles,
  useSharedButtonStyles,
} from "theme/sharedStyles";
import {
  downloadCSV,
  formatDate,
  generateCSV,
  userIsAdminOrPlatformAdmin,
} from "utils/generalUtils";
import { EditCategory } from "./editDialog/EditCategory";
import { useManageCategoriesStyles } from "./ManageCategories.styles";
import { IManageCategoriesProps } from "./ManageCategories.types";
import { ViewCategory } from "./viewDialog/ViewCategory";

export const ManageCategories = (
  props: IManageCategoriesProps = {},
): JSX.Element => {
  const { categories = [], isLoading: isLoadingProp = false } = props;
  const cellStyles = useManageCategoriesStyles();
  const colStyles = useAdminColumnStyles();
  const headerClasses = useSharedAdminContentHeaderStyles();
  const buttonStyles = useSharedButtonStyles();
  const currentUser: any = useSelector(
    (state: RootState) => state.currentUserSlice.value,
  );

  const [viewingCategory, setViewingCategory] = useState<ICategory | null>(
    null,
  );
  const [isViewDialogOpen, setIsViewDialogOpen] = useState<boolean>(false);
  const [editingCategory, setEditingCategory] = useState<ICategory | null>(
    null,
  );
  const [isEditDialogOpen, setIsEditDialogOpen] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState<boolean>(false);
  const [deletingCategory, setDeletingCategory] = useState<ICategory | null>(
    null,
  );
  const { filters, setFilter } = useAdminListFilters({
    cacheKey: "Categories",
    filterKeys: ["status"] as const,
  });
  const queryClient = useQueryClient();

  const columns: ITableColumn<ICategory>[] = useMemo(
    () => [
      {
        columnId: "title",
        displayValue: "Title",
        className: colStyles.normal,
        renderHeaderCell: () => "Title",
        compare: (a: ICategory, b: ICategory) => a.title.localeCompare(b.title),
        renderCell: (category: ICategory) => (
          <span className={cellStyles.titleText} title={category.title}>
            {category.title}
          </span>
        ),
      },
      {
        columnId: "description",
        displayValue: "Description",
        className: colStyles.wide,
        renderHeaderCell: () => "Description",
        compare: (a: ICategory, b: ICategory) =>
          a.description.localeCompare(b.description),
        renderCell: (category: ICategory) => (
          <Tooltip
            content={category.description}
            relationship="description"
            positioning="above"
          >
            <div
              className={cellStyles.descriptionText}
              title={category.description}
            >
              {category.description}
            </div>
          </Tooltip>
        ),
      },
      {
        columnId: "tags",
        displayValue: "# of KCs",
        className: colStyles.normal,
        renderHeaderCell: () => "# of KCs",
        compare: (a: ICategory, b: ICategory) =>
          (a.tags?.length || 0) - (b.tags?.length || 0),
        renderCell: (category: ICategory) => {
          const count = Array.isArray(category.tags) ? category.tags.length : 0;
          if (count === 0) {
            return <span>-</span>;
          }
          const tagsText = category.tags
            .map((tag: ITag) => `• ${tag.name}`)
            .join("\n");
          return (
            <Tooltip
              content={
                <span className={cellStyles.tooltipContent}>{tagsText}</span>
              }
              relationship="description"
              positioning="above"
            >
              <span>{count}</span>
            </Tooltip>
          );
        },
      },
      {
        columnId: "isPublic",
        displayValue: "Is Public",
        className: colStyles.medium,
        renderHeaderCell: () => "Is Public",
        compare: (a: ICategory, b: ICategory) =>
          Number(a.isPublic) - Number(b.isPublic),
        renderCell: (category: ICategory) => (
          <>{category.isPublic ? "Yes" : "No"}</>
        ),
      },
      {
        columnId: "updatedAt",
        displayValue: "Last Updated",
        className: colStyles.normal,
        renderHeaderCell: () => "Last Updated",
        compare: (a: ICategory, b: ICategory) => a.updatedAt - b.updatedAt,
        renderCell: (category: ICategory) => (
          <span>{formatDate(category.updatedAt)}</span>
        ),
      },
    ],
    [cellStyles, colStyles],
  );

  const {
    sortColumn,
    isSortDescending,
    sortItems,
    onSortColumnChanged,
    onClearColumnChanged,
  } = useAdminListSort<ICategory>({
    cacheKey: "Categories",
    defaultSortColumn: "title",
    defaultDescending: false,
  });

  const searchFields = useMemo(
    () => ["title", "description"] as (keyof ICategory)[],
    [],
  );
  const {
    searchQuery: internalSearchQuery,
    handleSearchChange,
    handleSearchValueChange,
    filteredItems,
  } = useAdminListSearch<ICategory>({
    items: categories,
    searchFields,
  });

  const fullyFilteredItems = useMemo(() => {
    let result = filteredItems;
    if (filters.status) {
      result = result.filter((cat) =>
        filters.status === "public"
          ? cat.isPublic === true
          : cat.isPublic !== true,
      );
    }
    return result;
  }, [filteredItems, filters.status]);

  const sortedItems = useMemo(
    () => sortItems(fullyFilteredItems, columns),
    [fullyFilteredItems, sortItems, columns],
  );

  const emptyStateMessage = useMemo(() => {
    const hasActiveFilters = internalSearchQuery || filters.status;
    return hasActiveFilters
      ? "No categories match the current filters."
      : 'No categories found. Use "Add New" to create one.';
  }, [internalSearchQuery, filters.status]);

  const {
    paginatedItems,
    selectedPageIndex,
    pageCount,
    handlePageChange,
    totalItemsCount,
    itemsPerPage,
  } = useAdminListPagination<ICategory>({
    items: sortedItems,
    itemsPerPage: 10,
  });

  const handleViewCategory = (category: ICategory) => {
    setViewingCategory(category);
    setIsViewDialogOpen(true);
  };

  const handleEditCategory = (category: ICategory) => {
    setEditingCategory(category);
    setIsEditDialogOpen(true);
  };

  const handleDeleteCategory = (category: ICategory) => {
    setDeletingCategory(category);
    setIsDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!deletingCategory) return;
    setIsSaving(true);
    try {
      await deleteCategory({ categoryId: deletingCategory.id });
      await queryClient.invalidateQueries("categories");
    } catch (error) {
      console.error("Failed to delete category:", error);
    } finally {
      setIsSaving(false);
      setIsDeleteDialogOpen(false);
      setDeletingCategory(null);
    }
  };

  const handleExport = useCallback((): void => {
    const csv = generateCSV(sortedItems, [
      { header: "Title", accessor: (c) => c.title },
      { header: "Description", accessor: (c) => c.description },
      {
        header: "# of KCs",
        accessor: (c) => String((c.tags || []).length),
      },
      { header: "Is Public", accessor: (c) => (c.isPublic ? "Yes" : "No") },
      { header: "Last Updated", accessor: (c) => formatDate(c.updatedAt) },
    ]);
    downloadCSV(csv, "categories.csv");
  }, [sortedItems]);

  const handleCloseEditDialog = () => {
    setIsEditDialogOpen(false);
    setEditingCategory(null);
  };

  const rowActions: AdminRowAction<ICategory>[] = useMemo(
    () => [
      {
        icon: <Eye20Regular />,
        tooltip: "View",
        onClick: handleViewCategory,
      },
      {
        icon: <Edit20Regular />,
        tooltip: "Edit",
        onClick: handleEditCategory,
        disabled: (category) =>
          !userIsAdminOrPlatformAdmin(currentUser) &&
          category.userId !== currentUser.id.toString(),
      },
      {
        icon: <Delete20Regular />,
        tooltip: "Delete",
        onClick: handleDeleteCategory,
        disabled: (category) =>
          !userIsAdminOrPlatformAdmin(currentUser) &&
          category.userId !== currentUser.id.toString(),
      },
    ],
    [currentUser],
  );

  return (
    <>
      <div className={headerClasses.root}>
        <div
          className={mergeClasses(
            headerClasses.titleRow,
            headerClasses.titleRowWithFilters,
          )}
        >
          <h1 className={headerClasses.title}>
            {findSidebarItem("categories")?.label ??
              "Knowledge Components Categories"}
          </h1>
          <div className={headerClasses.actions}>
            <button
              className={mergeClasses(
                headerClasses.actionButton,
                buttonStyles.secondaryButton,
              )}
              onClick={handleExport}
              type="button"
            >
              <ExportIcon /> Export
            </button>
            <button
              className={mergeClasses(
                headerClasses.actionButton,
                buttonStyles.primaryButton,
              )}
              onClick={() => {
                setEditingCategory(null);
                setIsEditDialogOpen(true);
              }}
              type="button"
            >
              <PlusIcon /> Add New
            </button>
          </div>
        </div>
        <div className={headerClasses.filterRow}>
          <div className={headerClasses.searchInputWrapper}>
            <SearchIcon className={headerClasses.searchIcon} />
            <input
              type="text"
              className={headerClasses.searchInput}
              placeholder="Search knowledge components categories..."
              value={internalSearchQuery}
              onChange={(e) => handleSearchValueChange(e.target.value)}
              aria-label="Search knowledge components categories..."
            />
          </div>
          <div className={headerClasses.filterSelectWrapper}>
            <select
              className={headerClasses.filterSelect}
              value={filters.status}
              onChange={(e) => setFilter("status", e.target.value)}
              aria-label="All Status"
            >
              <option value="">All Status</option>
              <option value="public">Public</option>
              <option value="notPublic">Not Public</option>
            </select>
            <ChevronIcon className={headerClasses.filterChevron} />
          </div>
        </div>
      </div>
      <AdminListPage<ICategory>
        title="Categories"
        items={categories}
        columns={columns}
        isLoading={isLoadingProp || isSaving}
        headerActions={[]}
        searchPlaceholder="Search categories..."
        searchQuery={internalSearchQuery}
        onSearchChange={handleSearchChange}
        hideHeader={true}
        rowActions={rowActions}
        getRowId={(category) => category.id}
        sortColumn={sortColumn}
        isSortDescending={isSortDescending}
        onSortChange={onSortColumnChanged}
        onSortClear={onClearColumnChanged}
        paginatedItems={paginatedItems}
        selectedPageIndex={selectedPageIndex}
        pageCount={pageCount}
        totalItemsCount={totalItemsCount}
        itemsPerPage={itemsPerPage}
        onPageChange={handlePageChange}
        emptyStateMessage={emptyStateMessage}
      />

      {isViewDialogOpen && viewingCategory && (
        <ViewCategory
          isOpen={isViewDialogOpen}
          onDismiss={() => {
            setIsViewDialogOpen(false);
            setViewingCategory(null);
          }}
          category={viewingCategory}
        />
      )}

      {isEditDialogOpen && (
        <EditCategory
          isOpen={isEditDialogOpen}
          onDismiss={() => {
            handleCloseEditDialog();
          }}
          categories={categories}
          category={editingCategory}
        />
      )}

      <ActionDialog
        isVisible={isDeleteDialogOpen}
        title="Delete Category"
        message={`Are you sure you want to permanently delete "${deletingCategory?.title}"? This action cannot be undone.`}
        confirmationMessage="Delete"
        dismissMessage="Cancel"
        onConfirmation={handleConfirmDelete}
        onDismiss={() => {
          setIsDeleteDialogOpen(false);
          setDeletingCategory(null);
        }}
      />
    </>
  );
};
